import React, { useState } from 'react';
import { X, Camera, Lock, Globe, Eye, EyeOff } from 'lucide-react';
import './CreateAlbumModal.css';

const CreateAlbumModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_public: true,
    password: '',
    require_password: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Please enter an album name');
      return;
    }

    if (formData.require_password && !formData.password.trim()) {
      alert('Please enter a password for this album');
      return;
    }

    setCreating(true);

    // Prepare the data to send
    const albumData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      is_public: formData.is_public,
      password: formData.require_password ? formData.password : null
    };

    try {
      await onCreate(albumData);
    } catch (error) {
      console.error('Error creating album:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>
            <Camera size={24} />
            Create New Album
          </h2>
          <button className="btn-close" onClick={onClose} disabled={creating}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="name">Album Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter album name..."
              maxLength={100}
              required
              disabled={creating}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Optional description for your album..."
              rows={3}
              maxLength={500}
              disabled={creating}
            />
          </div>

          <div className="form-group">
            <label className="form-section-label">Privacy Settings</label>
            
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="is_public"
                  checked={formData.is_public === true}
                  onChange={() => setFormData(prev => ({ ...prev, is_public: true }))}
                  disabled={creating}
                />
                <div className="radio-content">
                  <div className="radio-icon">
                    <Globe size={20} />
                  </div>
                  <div>
                    <div className="radio-title">Public</div>
                    <div className="radio-description">Anyone can view this album</div>
                  </div>
                </div>
              </label>

              <label className="radio-option">
                <input
                  type="radio"
                  name="is_public"
                  checked={formData.is_public === false}
                  onChange={() => setFormData(prev => ({ ...prev, is_public: false }))}
                  disabled={creating}
                />
                <div className="radio-content">
                  <div className="radio-icon">
                    <Lock size={20} />
                  </div>
                  <div>
                    <div className="radio-title">Private</div>
                    <div className="radio-description">Only you can view this album</div>
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-option">
              <input
                type="checkbox"
                name="require_password"
                checked={formData.require_password}
                onChange={handleInputChange}
                disabled={creating}
              />
              <div className="checkbox-content">
                <div className="checkbox-title">Password Protection</div>
                <div className="checkbox-description">Require a password to access this album</div>
              </div>
            </label>

            {formData.require_password && (
              <div className="password-field">
                <div className="password-input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter album password..."
                    disabled={creating}
                  />
                  <button
                    type="button"
                    className="btn-toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={creating}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button 
              type="button" 
              className="btn-cancel" 
              onClick={onClose}
              disabled={creating}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-create"
              disabled={creating || !formData.name.trim()}
            >
              {creating ? (
                <>
                  <div className="spinner-small"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Camera size={18} />
                  Create Album
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAlbumModal;